import React from 'react';
import styled from 'styled-components';
import StyledModal from '../../../../components/modal/styledModals/StyledModal';
import { styles } from '../../../../styles/themes/style';
import SvgFragment from '../../../../fragments/SvgFragment';

/**
 * Confirmation for the address lookup.
 *
 * This spends credits, so it gets a deliberate step rather than an inline "are you
 * sure?" swapped into the panel — the inline version put the cost and the balance in
 * two different places and made "Yes, spend 18" the same button the user had just
 * pressed by accident.
 *
 * Not enough credits is a state of this dialog, not a separate one: the action stays
 * available, because the balance shown here comes from cached workspace state and the
 * server settles the real charge. Blocking on a stale number would lock people out of
 * something they can afford.
 */

interface Props {
  show: boolean;
  onHide: () => void;
  onConfirm: () => void;
  /** Leads with no address yet — the number we would look up. */
  remaining: number;
  /** Upper bound: nothing is charged for an address we fail to find. */
  estimatedCredits: number;
  balance: number;
  running?: boolean;
  onBuyCredits: () => void;
}

const FindEmailsModal: React.FC<Props> = ({
  show, onHide, onConfirm, remaining, estimatedCredits, balance, running, onBuyCredits,
}) => {
  const shortBy = Math.max(0, estimatedCredits - balance);

  return (
    <StyledModal show={show} onHide={onHide} width="560px" backdrop>
      <Body>
        <Header>
          <Title>Find {remaining} missing email{remaining === 1 ? '' : 's'}</Title>
          <CloseBtn type="button" onClick={onHide} aria-label="Close">
            <SvgFragment type="close" />
          </CloseBtn>
        </Header>

        <Lead>
          We&apos;ll search for email addresses for {remaining} lead{remaining === 1 ? '' : 's'}.
          You&apos;re only charged for emails we successfully find.
        </Lead>

        <Cards>
          <Card>
            <CardHead>
              <CardIcon><CoinIcon /></CardIcon>
              <CardTitle>Estimated cost</CardTitle>
            </CardHead>
            <CardValue>
              Up to {estimatedCredits} credit{estimatedCredits === 1 ? '' : 's'}
            </CardValue>
          </Card>

          <Card>
            <CardHead>
              <CardIcon><BalanceIcon /></CardIcon>
              <CardTitle>Your balance</CardTitle>
            </CardHead>
            <CardValue>{balance} credit{balance === 1 ? '' : 's'} available</CardValue>
          </Card>
        </Cards>

        {/* Says the shortfall as a number to act on. "Insufficient credits" tells the
            user they are stuck; "you need 8 more" tells them what to do. */}
        {shortBy > 0 && (
          <Warn>
            <WarnIcon aria-hidden><AlertIcon /></WarnIcon>
            <span>
              You need <strong>{shortBy}</strong> more credit{shortBy === 1 ? '' : 's'} to find all
              missing emails.{' '}
              <BuyLink type="button" onClick={onBuyCredits}>Buy credits</BuyLink>
            </span>
          </Warn>
        )}

        <Footer>
          <GhostBtn type="button" onClick={onHide} disabled={running}>Cancel</GhostBtn>
          <PrimaryBtn type="button" onClick={onConfirm} disabled={running}>
            {running ? 'Finding…' : 'Find missing emails'}
          </PrimaryBtn>
        </Footer>
      </Body>
    </StyledModal>
  );
};

export default FindEmailsModal;

// ─── Icons ────────────────────────────────────────────────────────────────────

const svg = (d: React.ReactNode) => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"
    style={{ display: 'block' }}>{d}</svg>
);

const CoinIcon = () => svg(<><circle cx="12" cy="12" r="8.5" /><path d="M14.5 9.3a3 3 0 00-2.5-1.1c-1.5 0-2.5.8-2.5 1.9s1 1.6 2.5 1.9 2.5.8 2.5 1.9-1 1.9-2.5 1.9a3 3 0 01-2.5-1.1" /><path d="M12 6.5v11" /></>);
const BalanceIcon = () => svg(<><circle cx="10" cy="12" r="6.5" /><path d="M15.5 6.2a6.5 6.5 0 010 11.6" /></>);
const AlertIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ display: 'block' }}>
    <path d="M10.3 3.9L1.8 18a2 2 0 001.7 3h17a2 2 0 001.7-3L13.7 3.9a2 2 0 00-3.4 0z" />
    <path d="M12 9v4M12 17h.01" />
  </svg>
);

// ─── Styles ───────────────────────────────────────────────────────────────────

const ACCENT = styles.colors.primary600;

const Body = styled.div`
  display: flex; flex-direction: column; gap: 16px;
  padding: 24px 26px;
`;

const Header = styled.div`
  display: flex; align-items: flex-start; justify-content: space-between; gap: 12px;
`;

const Title = styled.div`
  font-family: NunitoSans; font-size: 22px; font-weight: 800; color: #0f172a;
  line-height: 1.25;
`;

const CloseBtn = styled.button`
  flex: 0 0 auto;
  border: none; background: none; cursor: pointer; padding: 2px;
  color: #94a3b8;
  &:hover { color: #475569; }
`;

const Lead = styled.p`
  margin: 0;
  font-family: NunitoSans; font-size: 14px; line-height: 1.55; color: #475569;
`;

const Cards = styled.div`
  display: flex; gap: 12px;
`;

const Card = styled.div`
  flex: 1 1 0; min-width: 0;
  display: flex; flex-direction: column; gap: 4px;
  border: 1px solid #e2e8f0; border-radius: 10px;
  padding: 12px 14px;
`;

const CardHead = styled.div`
  display: flex; align-items: center; gap: 8px;
`;

const CardIcon = styled.span`
  flex: 0 0 auto; display: inline-flex; color: ${ACCENT};
`;

const CardTitle = styled.span`
  font-family: NunitoSans; font-size: 13px; font-weight: 700; color: #0f172a;
`;

const CardValue = styled.span`
  font-family: NunitoSans; font-size: 13px; color: #94a3b8;
`;

const Warn = styled.div`
  display: flex; align-items: flex-start; gap: 8px;
  background: #fffbeb; border-radius: 8px; padding: 10px 12px;
  font-family: NunitoSans; font-size: 13px; color: #78350f; line-height: 1.5;
  strong { font-weight: 800; }
`;

const WarnIcon = styled.span`
  flex: 0 0 auto; margin-top: 1px; color: #f59e0b;
`;

const BuyLink = styled.button`
  border: none; background: none; padding: 0; cursor: pointer;
  font: inherit; font-weight: 800; color: ${ACCENT}; text-decoration: underline;
`;

const Footer = styled.div`
  display: flex; align-items: center; justify-content: flex-end; gap: 12px;
  margin-top: 4px;
`;

const GhostBtn = styled.button`
  border: none; background: none; cursor: pointer; padding: 9px 8px;
  font-family: NunitoSans; font-size: 13px; font-weight: 700; color: #64748b;
  &:hover:not(:disabled) { color: #334155; }
  &:disabled { opacity: .6; cursor: default; }
`;

const PrimaryBtn = styled.button`
  border: none; border-radius: 8px; padding: 11px 20px;
  background: ${ACCENT}; color: #fff; cursor: pointer;
  font-family: NunitoSans; font-size: 13px; font-weight: 700;
  &:disabled { opacity: .6; cursor: default; }
`;
